import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, CreditCard, Users, Zap, ShieldCheck, HardDrive, ExternalLink, CheckCircle2, XCircle, Ticket, Clock, X, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const GUMROAD_BASE = "https://barndoorsoftware.gumroad.com/l";

const FeatureRequestTab = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [billingStatus, setBillingStatus] = useState(null);
  const [credits, setCredits] = useState(0);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  useEffect(() => {
    if (tenantId) { fetchStatus(); fetchCredits(); }
  }, [tenantId]);

  const fetchCredits = async () => {
    try {
      const res = await api.get(`/billing/sale-entry-credits/${tenantId}`);
      setCredits(res.data.credits || 0);
    } catch { /* ignore */ }
  };

  const fetchStatus = async () => {
    try {
      const res = await api.get(`/billing/tenant-status/${tenantId}`);
      setBillingStatus(res.data);
    } catch {
      setBillingStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const openCheckoutDialog = (permalink) => {
    setCheckoutUrl(`${GUMROAD_BASE}/${permalink}?wanted=true`);
  };

  const openCheckout = async (productKey) => {
    try {
      const tenant = await api.get(`/tenants/${tenantId}`);
      const email = tenant.data?.billing_email || tenant.data?.email || '';
      const res = await api.get(`/billing/checkout-link/${productKey}?email=${encodeURIComponent(email)}`);
      const url = res.data.checkout_url + (res.data.checkout_url.includes('?') ? '&' : '?') + 'wanted=true';
      setCheckoutUrl(url);
    } catch {
      toast.error('Failed to open checkout');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const billing = billingStatus?.billing || {};
  const features = billingStatus?.features || {};
  const isActive = billingStatus?.subscription_active;

  const StatusIcon = ({ active }) => active
    ? <CheckCircle2 className="w-4 h-4 text-green-600" />
    : <XCircle className="w-4 h-4 text-gray-400" />;

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" /> Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">Subscription Status:</span>
            <Badge className={isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <StatusIcon active={billing.base_fee?.active} />
              <span>Base Plan ({billing.base_fee?.recurrence === 'yearly' ? 'Annual' : 'Monthly'})</span>
            </div>
            <div className="flex items-center gap-2">
              <StatusIcon active={billing.additional_user?.active} />
              <span>Additional Users</span>
            </div>
          </div>
          {!isActive && (
            <Button onClick={() => openCheckout('base_fee')} className="bg-primary hover:bg-primary/90 text-white">
              <CreditCard className="w-4 h-4 mr-2" /> Subscribe Now
            </Button>
          )}
        </CardContent>
      </Card>

      {/* User Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" /> Users
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Base plan includes 5 users. Additional users are $15/month ($180/year) each.
          </p>
          <div className="flex items-center gap-2">
            <StatusIcon active={billing.additional_user?.active} />
            <span className="text-sm">Additional user subscription: <strong>{billing.additional_user?.active ? 'Active' : 'Not subscribed'}</strong></span>
          </div>
          {!billing.additional_user?.active && (
            <Button variant="outline" size="sm" onClick={() => openCheckout('additional_user')}>
              <ExternalLink className="w-4 h-4 mr-2" /> Add More Users
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Premium Features */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" /> Premium Features
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Premium Support */}
          <div className="border rounded p-4 flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <LifeBuoy className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Premium Support</span>
                {billing.premium_support?.status === 'pending_activation' ? (
                  <Badge className="bg-yellow-100 text-yellow-700">Pending Activation</Badge>
                ) : (
                  <Badge className={features.premium_support ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                    {features.premium_support ? 'Active' : 'Inactive'}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 ml-6">
                {billing.premium_support?.status === 'pending_activation'
                  ? 'Purchased — awaiting activation by our team'
                  : 'Priority support with dedicated response times and direct access to our team'}
              </p>
            </div>
            {!features.premium_support && !billing.premium_support?.paid && (
              <Button variant="outline" size="sm" onClick={() => openCheckout('premium_support')}>
                <ExternalLink className="w-3 h-3 mr-1" /> Enable
              </Button>
            )}
          </div>

          {/* Branding Control */}
          <div className="border rounded p-4 flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Branding Control</span>
                {billing.branding_control?.status === 'pending_activation' ? (
                  <Badge className="bg-yellow-100 text-yellow-700">Pending Activation</Badge>
                ) : (
                  <Badge className={features.branding_control ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                    {features.branding_control ? 'Active' : 'Inactive'}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 ml-6">
                {billing.branding_control?.status === 'pending_activation'
                  ? 'Purchased — awaiting configuration by our team'
                  : 'Remove "Powered by Green Door" branding from emails and documents'}
              </p>
            </div>
            {!features.branding_control && !billing.branding_control?.paid && (
              <Button variant="outline" size="sm" onClick={() => openCheckout('branding_control')}>
                <ExternalLink className="w-3 h-3 mr-1" /> Enable
              </Button>
            )}
          </div>

          {/* Payment Processing */}
          <div className="border rounded p-4 flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Payment Processing</span>
                {billing.payment_processing?.status === 'pending_activation' ? (
                  <Badge className="bg-yellow-100 text-yellow-700">Pending Activation</Badge>
                ) : (
                  <Badge className={features.payment_processing ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                    {features.payment_processing ? 'Active' : 'Inactive'}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 ml-6">
                {billing.payment_processing?.status === 'pending_activation'
                  ? 'Purchased — awaiting configuration by our team'
                  : 'If you have a Stripe Account, accept payments from owners through the Owner Portal'}
              </p>
            </div>
            {!features.payment_processing && !billing.payment_processing?.paid && (
              <Button variant="outline" size="sm" onClick={() => openCheckout('payment_processing')}>
                <ExternalLink className="w-3 h-3 mr-1" /> Enable
              </Button>
            )}
          </div>

          {/* Unlimited Storage */}
          <div className="border rounded p-4 flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Unlimited Storage</span>
                {billing.unlimited_storage?.status === 'pending_activation' ? (
                  <Badge className="bg-yellow-100 text-yellow-700">Pending Activation</Badge>
                ) : (
                  <Badge className={features.unlimited_storage ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                    {features.unlimited_storage ? 'Active' : 'Inactive'}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 ml-6">
                {billing.unlimited_storage?.status === 'pending_activation'
                  ? 'Purchased — awaiting configuration by our team'
                  : 'Remove storage limits for documents, photos, and files'}
              </p>
            </div>
            {!features.unlimited_storage && !billing.unlimited_storage?.paid && (
              <Button variant="outline" size="sm" onClick={() => openCheckout('unlimited_storage')}>
                <ExternalLink className="w-3 h-3 mr-1" /> Enable
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sale Entry Credits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5" /> Sale Entry Credits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">Remaining Credits:</span>
            <Badge className={credits > 0 ? 'bg-green-100 text-green-700 text-base px-3' : 'bg-red-100 text-red-700 text-base px-3'}>
              {credits}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Each horse entered into a sale uses 1 credit ($50 per entry). Purchase credit packs below.
          </p>
          {credits === 0 && (
            <div className="border border-red-200 rounded p-3 bg-red-50 text-sm text-red-700">
              No credits remaining. You must purchase credits to enter horses into sales.
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div onClick={() => openCheckoutDialog('wtzgpd')} className="border rounded-lg p-4 text-center hover:border-primary hover:bg-green-50/50 transition-colors cursor-pointer">
              <p className="font-semibold text-sm">Pack of 5</p>
              <p className="text-xl font-bold mt-1">$250</p>
              <p className="text-xs text-muted-foreground mt-1">5 Sale Entries</p>
            </div>
            <div onClick={() => openCheckoutDialog('jrpvs')} className="border rounded-lg p-4 text-center hover:border-primary hover:bg-green-50/50 transition-colors cursor-pointer">
              <p className="font-semibold text-sm">Pack of 10</p>
              <p className="text-xl font-bold mt-1">$500</p>
              <p className="text-xs text-muted-foreground mt-1">10 Sale Entries</p>
            </div>
            <div onClick={() => openCheckoutDialog('posnoe')} className="border rounded-lg p-4 text-center hover:border-primary hover:bg-green-50/50 transition-colors cursor-pointer">
              <p className="font-semibold text-sm">Pack of 20</p>
              <p className="text-xl font-bold mt-1">$1,000</p>
              <p className="text-xs text-muted-foreground mt-1">20 Sale Entries</p>
            </div>
            <div onClick={() => openCheckoutDialog('cqmqlk')} className="relative border-2 border-primary rounded-lg p-4 text-center hover:bg-green-50/50 transition-colors cursor-pointer">
              <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] px-2 py-0.5 pointer-events-none whitespace-nowrap">Best Value</Badge>
              <p className="font-semibold text-sm">Pack of 50</p>
              <p className="text-xl font-bold mt-1">$2,250</p>
              <p className="text-xs text-muted-foreground mt-1">50 Sale Entries</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground text-center">
            Need help with your plan? Contact us at <a href="mailto:support@mygreendoorapp.com" className="text-primary hover:underline">support@mygreendoorapp.com</a>
          </p>
        </CardContent>
      </Card>

      {/* Checkout Dialog (iframe) */}
      <Dialog open={!!checkoutUrl} onOpenChange={(open) => { if (!open) { setCheckoutUrl(null); fetchCredits(); fetchStatus(); } }}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] p-0 gap-0">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>Complete Purchase</DialogTitle>
          </DialogHeader>
          {checkoutUrl && (
            <iframe
              src={checkoutUrl}
              className="w-full border-0"
              style={{ height: '70vh' }}
              title="Gumroad Checkout"
              allow="payment"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeatureRequestTab;
